import { useState, useMemo } from 'react'
import { Button } from '../../shared/components/Button'
import RadioGroup from '../../shared/components/RadioGroup'
import { FormInput } from '../../shared/components/Fieldset'
import { useCurrency } from '../../util/CurrencyContext'
import { useTranslation } from 'react-i18next'
import {
    FaArrowRight,
    FaCheckCircle,
    FaFire,
    FaHeart,
    FaRegLightbulb,
    FaRegMoon,
    FaRegStar,
    FaRegSun,
    FaStar,
    FaFilter,
    FaGlobe,
    FaHandHoldingHeart,
    FaHome,
    FaSchool,
    FaTree,
    FaWater,
    FaUserMd,
    FaBook,
    FaNewspaper,
    FaMosque,
    FaCoins,
} from 'react-icons/fa'
import { languageUtil } from '../../util/languageUtils'
import CurrencyFormat from './CurrencyFormat'

export default function QuickDonationCard({ onDonate }) {
    const { t } = useTranslation()
    const { language } = languageUtil()

    const [showWarning, setShowWarning] = useState(false)
    const [selectedFrequency, setSelectedFrequency] = useState('single')
    const { currency, currencySymbol, currencyRate } = useCurrency()

    const [selectedCustomProject, setSelectedCustomProject] = useState('where_most_needed')
    const [customAmountDisplay, setCustomAmountDisplay] = useState('')
    const [customAmountOriginal, setCustomAmountOriginal] = useState('') // Original amount stored

    const amountOptions = [30, 60, 100, 200]

    const frequencyOptions = [
        {
            label: t('donation.frequency.oneTime'),
            value: 'single',
        },
        {
            label: t('donation.frequency.monthly'),
            value: 'monthly',
        },
    ]

    const allOptions = [
        {
            id: null,
            title: 'Where Most Needed',
            title_en: 'Where Most Needed',
            title_ar: 'حيث تشتد الحاجة',
            type: 'where_most_needed',
            donation_type: 'where_most_needed',
            isWhereMostNeeded: true,
        },
        {
            id: 3,
            title: 'Zakah',
            title_en: 'Zakah',
            title_ar: 'الزكاة',
            type: 'religious_dues',
            donation_type: 'religious_due',
            isWhereMostNeeded: false,
        },
        {
            id: 2,
            title: 'Sadaqah',
            title_en: 'Sadaqah',
            title_ar: 'صدقة',
            type: 'religious_dues',
            donation_type: 'religious_due',
            isWhereMostNeeded: false,
        },
    ]

    const getOriginalAmount = () => {
        const displayValue = parseFloat(customAmountDisplay)
        if (isNaN(displayValue)) return 0
        return displayValue / currencyRate
    }

    // Helper function to convert display amount to original
    const handleCustomAmountChange = displayValue => {
        setCustomAmountDisplay(displayValue)
        const originalValue = parseFloat(displayValue) // currencyRate
        setCustomAmountOriginal(isNaN(originalValue) ? '' : originalValue.toString())
        setShowWarning(false)
    }

    // When quick amount buttons are clicked
    const handleQuickAmountClick = originalAmount => {
        // Convert to display amount
        const displayAmount = (originalAmount * currencyRate).toFixed(2)
        setCustomAmountDisplay(displayAmount)
        setCustomAmountOriginal(displayAmount)

        setShowWarning(false)
    }

    // In handleDonate, use the original amount
    const handleDonate = () => {
        const amount = parseFloat(customAmountOriginal)
        if (!amount || amount <= 0) {
            setShowWarning(true)
            return
        }

        const donationPayload = {
            amount: amount,
            frequent_option: selectedFrequency,
            parent_id: selectedCustomProject?.id,
            donation_type: selectedCustomProject?.donation_type || 'where_most_needed',
            isCustomAmount: true,
            isWhereMostNeeded: selectedCustomProject?.isWhereMostNeeded || false,
        }

        onDonate(donationPayload)
    }

    const handleCustomProjectSelect = project => {
        setSelectedCustomProject(project)
    }

    const canSubmit = () => {
        const originalAmount = getOriginalAmount()
        const hasValidAmount = originalAmount > 0
        const hasProject = selectedCustomProject !== null

        return hasValidAmount && hasProject
    }

    return (
        <div className='w-full p-6 border border-yellow-100 shadow-2xl md:p-8 bg-gradient-to-br from-white via-yellow-50 to-white rounded-2xl'>
            {/* Header with frequency pills */}
            <div className='relative mb-8'>
                <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
                    {/* Title with frequency pills to the right */}
                    <div className='flex-1'>
                        <div className='flex items-start justify-between gap-4'>
                            <div className='relative'>
                                <div className='absolute w-6 h-6 bg-yellow-400 rounded-full -top-2 -left-2 animate-pulse'></div>
                                <h3 className='relative text-2xl font-bold text-gray-900 md:text-3xl'>
                                    <span className='relative z-10'>
                                        {t('homepage.donation.title') || 'Make Your Impact'}
                                    </span>
                                    <span className='absolute left-0 w-full h-3 transform bg-yellow-400 rounded-full opacity-50 -bottom-1 -rotate-1'></span>
                                </h3>
                                <div className='absolute w-4 h-4 bg-yellow-300 rounded-full -top-2 -right-2'></div>
                            </div>

                            {/* Frequency pills - desktop */}
                            <div className='flex-shrink-0 hidden md:block'>
                                <div className='flex items-center gap-2 mb-1'>
                                    <FaHeart className='text-sm text-yellow-400' />
                                    <span className='text-xs font-medium text-gray-700'>
                                        {t('homepage.donation.frequency')}
                                    </span>
                                </div>
                                <div className='flex gap-1'>
                                    {frequencyOptions.map(freq => (
                                        <button
                                            key={freq.value}
                                            onClick={() => setSelectedFrequency(freq.value)}
                                            className={`
                                    px-3 py-1 rounded-full border transition-all duration-300
                                    text-xs font-medium
                                    ${
                                        selectedFrequency === freq.value
                                            ? 'border-yellow-500 bg-yellow-500 text-gray-900 shadow-sm'
                                            : 'border-gray-300 text-gray-700 hover:border-yellow-400 hover:bg-yellow-50'
                                    }
                                `}
                                        >
                                            {freq.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <p className='mt-2 text-gray-600 lg:pr-48'>
                            {t('homepage.donation.subtitle') ||
                                'Select an amount that matches your generosity'}
                        </p>

                        {/* Mobile frequency selector */}
                        <div className='mt-4 md:hidden'>
                            <div className='flex items-center gap-2 mb-2'>
                                <FaHeart className='text-sm text-yellow-400' />
                                <label className='text-sm font-medium text-gray-700'>
                                    {t('homepage.donation.frequency') || 'Donation Frequency'}
                                </label>
                            </div>
                            <div className='flex gap-2'>
                                {frequencyOptions.map(freq => (
                                    <button
                                        key={freq.value}
                                        onClick={() => setSelectedFrequency(freq.value)}
                                        className={`
                                flex-1 py-2 rounded-lg border transition-all duration-300
                                text-sm font-medium
                                ${
                                    selectedFrequency === freq.value
                                        ? 'border-yellow-500 bg-yellow-50 text-gray-900'
                                        : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-50 text-gray-700'
                                }
                            `}
                                    >
                                        {freq.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Amount Section */}
            <div className='mb-8'>
                {/* Everything in one row */}
                <div className='flex flex-col items-start gap-4 md:flex-row md:items-end'>
                    {/* Quick Amount Buttons */}
                    <div className=''>
                        <div className='flex items-center gap-2 mb-2'>
                            <FaCoins className='text-sm text-yellow-400' />
                            <label className='text-xs font-medium text-gray-700'>
                                {t('donation.selectAmount') || 'Quick Amount'}
                            </label>
                        </div>
                        <div className='flex gap-2'>
                            {amountOptions.map(amount => {
                                return (
                                    <button
                                        key={amount}
                                        onClick={() => handleQuickAmountClick(amount)}
                                        className={`
                        flex-1 py-2 px-3 rounded-lg border transition-all duration-300
                        text-sm font-medium
                        ${
                            customAmountOriginal === amount.toString()
                                ? 'border-yellow-500 bg-gradient-to-r from-yellow-50 to-yellow-100 shadow-sm text-yellow-700'
                                : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-50 text-gray-700'
                        }
                    `}
                                    >
                                        <CurrencyFormat amount={amount} />
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    {/* Amount input - shows converted amount */}
                    <div className='flex-1 min-w-full md:min-w-0'>
                        <div className='flex items-center gap-2 mb-1'>
                            <FaStar className='text-sm text-yellow-400' />
                            <label className='text-xs font-medium text-gray-700 whitespace-nowrap'>
                                {t('donation.enterAmount') || 'Custom Amount'}
                            </label>
                        </div>

                        <div className='relative group'>
                            <div className='absolute inset-0 transition-opacity duration-300 opacity-0 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl blur group-hover:opacity-50'></div>
                            <FormInput
                                type='number'
                                // Show the converted amount in the input field
                                value={customAmountDisplay}
                                placeholder={`${t('donation.customAmountPlaceholder', {
                                    currency: currency,
                                })}`}
                                onChange={e => handleCustomAmountChange(e.target.value)}
                                className={`
                relative w-full pl-12 pr-3 py-2 text-base font-semibold 
                bg-white border-2 rounded-xl focus:outline-none focus:ring-2
                transition-all duration-300
                ${
                    customAmountDisplay
                        ? 'border-yellow-500 ring-2 ring-yellow-300'
                        : 'border-yellow-200 focus:border-yellow-400 focus:ring-yellow-300'
                }
                group-hover:border-yellow-300
            `}
                                min='0.01'
                                step='0.01'
                            />
                            <div className='absolute font-bold text-gray-700 transform -translate-y-1/2 left-3 top-1/2'>
                                {currencySymbol}
                            </div>
                        </div>
                    </div>

                    {/* Project selection */}
                    <div className='flex-1 min-w-full md:min-w-0'>
                        <div className='flex items-center gap-2 mb-1'>
                            <FaHandHoldingHeart className='text-sm text-yellow-400' />
                            <label className='block text-xs font-medium text-gray-700 whitespace-nowrap'>
                                {t('donation.selectProject') || 'Select Project'}
                            </label>
                        </div>

                        <div className='relative'>
                            <select
                                value={
                                    selectedCustomProject?.id === null
                                        ? 'where-most-needed'
                                        : selectedCustomProject?.id || ''
                                }
                                onChange={e => {
                                    const selectedId = e.target.value
                                    const selectedProject = allOptions.find(project => {
                                        if (
                                            project.id === null &&
                                            selectedId === 'where-most-needed'
                                        ) {
                                            return true
                                        }
                                        return project.id?.toString() === selectedId
                                    })
                                    handleCustomProjectSelect(selectedProject)
                                }}
                                className='w-full py-2 pl-3 pr-8 text-sm transition-all duration-300 bg-white border-2 border-yellow-200 appearance-none cursor-pointer rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-yellow-400 hover:border-yellow-300'
                            >
                                {allOptions.map(project => (
                                    <option
                                        key={project.id || 'where-most-needed'}
                                        value={
                                            project.id === null ? 'where-most-needed' : project.id
                                        }
                                        className='py-1'
                                    >
                                        {project[`title_${language}`]}
                                    </option>
                                ))}
                            </select>

                            <div className='absolute transform -translate-y-1/2 pointer-events-none right-2 top-1/2'>
                                <svg
                                    className='w-4 h-4 text-gray-500'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M19 9l-7 7-7-7'
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Donate button */}
                    <div className='w-full md:w-auto'>
                        <button
                            onClick={handleDonate}
                            disabled={!canSubmit}
                            className={`
                            group relative w-full md:w-auto py-2 px-4 rounded-xl
                            text-sm font-bold text-gray-900
                            transition-all duration-500
                            ${
                                canSubmit()
                                    ? 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 shadow-lg hover:shadow-xl'
                                    : 'bg-gray-200 cursor-not-allowed'
                            }
                            transform hover:scale-[1.02] active:scale-[0.98]
                            overflow-hidden
                            h-[42px] /* Match the height of other inputs */
                        `}
                        >
                            {/* Animated background */}
                            <div className='absolute inset-0 transition-opacity duration-300 opacity-0 bg-gradient-to-r from-yellow-500 to-yellow-600 group-hover:opacity-100'></div>

                            {/* Button content */}
                            <div className='relative z-10 flex items-center justify-center gap-2'>
                                <FaHeart
                                    className={`text-sm ${canSubmit() ? 'text-red-500' : 'text-gray-500'}`}
                                />
                                <span className='whitespace-nowrap'>
                                    {canSubmit()
                                        ? t('donation.donateNow') || 'Donate'
                                        : t('donation.selectAmount') || 'Select Amount'}
                                </span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Validation message - appears below the row */}
                {showWarning && (
                    <div className='p-3 mt-3 border border-yellow-300 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl animate-pulse'>
                        <div className='flex items-center gap-2 text-yellow-700'>
                            <div className='w-2 h-2 bg-yellow-400 rounded-full'></div>
                            <span className='text-sm font-medium'>
                                {t('donation.amountRequired') ||
                                    'Please select or enter a donation amount'}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
